/**
 * ShiftCloseDialog — cashier closes their shift.
 *
 * PDF auto-open after close now uses shiftsService.openZReportPdf (axios
 * + blob) instead of window.open with a constructed URL. The old approach
 * hit the wrong origin and produced 404s.
 */
import { useCallback, useMemo, useState, useEffect } from "react";
import DenominationCounter from "./DenominationCounter";
import {
  StopCircle,
  Wallet,
  TrendingUp,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  AlertOctagon,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useCloseShift } from "@/hooks/useShifts";
import { shiftsService } from "@/services/shiftsService";
import { useQuery } from "@tanstack/react-query";
import api from "@/services/api";
import type { Shift } from "@/types/shifts.types";
import { useOfflineOptional } from "@/context/OfflineProvider";
import { useOutbox } from "@/hooks/useOutbox";
import { curSymbol, formatMajor, getCurrencyCode } from "@/lib/currency";

const fmtGHS = (major: number) => formatMajor(major || 0);

const minorToCedis = (m: number | null | undefined) => (m || 0) / 100;

interface ShiftCloseDialogProps {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  shift: Shift;
  isAdminClose?: boolean;
}

function classifyVariance(amountCedis: number) {
  const abs = Math.abs(amountCedis);
  if (abs <= 5) return "balanced";
  if (abs <= 50) return "minor";
  return "significant";
}

interface PreviewSnapshot {
  cashTotalCedis: number | null;
  expectedCashCedis: number | null;
  /** Blind close: the cashier doesn't see expected cash or the variance. */
  blind?: boolean;
}

export default function ShiftCloseDialog({
  open,
  onOpenChange,
  shift,
  isAdminClose = false,
}: ShiftCloseDialogProps) {
  const [cashCount, setCashCount] = useState("");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [byNotes, setByNotes] = useState(false);
  const onCounted = useCallback((major: number) => setCashCount(major ? major.toFixed(2) : ""), []);

  const closeMutation = useCloseShift();

  // Best-effort preview endpoint. If it doesn't exist (404), we silently
  // fall back to "Calculated at close".
  const { data: preview } = useQuery<PreviewSnapshot | null>({
    queryKey: ["shift-close-preview", shift._id],
    queryFn: async () => {
      try {
        const res = await api.get<PreviewSnapshot>(
          `/shifts/${shift._id}/preview`,
        );
        return res.data ?? null;
      } catch {
        return null;
      }
    },
    enabled: open,
    staleTime: 30_000,
    retry: false,
  });

  function reset() {
    setCashCount("");
    setReason("");
    setNotes("");
  }
  function close(o: boolean) {
    if (!o) reset();
    onOpenChange(o);
  }
  useEffect(() => {
    if (!open) reset();
  }, [open]);

  const openingCashCedis = minorToCedis(shift.openingCashMinor);
  const blind = !!preview?.blind;
  const expectedFromPreview = blind ? undefined : preview?.expectedCashCedis ?? undefined;
  const liveCountCedis = Number(cashCount) || 0;

  const liveVariance = useMemo(() => {
    if (typeof expectedFromPreview !== "number") return null;
    return liveCountCedis - expectedFromPreview;
  }, [liveCountCedis, expectedFromPreview]);

  const varianceClass = useMemo(() => {
    if (liveVariance === null) return null;
    return classifyVariance(liveVariance);
  }, [liveVariance]);

  const requiresReason = varianceClass === "significant";
  const reasonMissing = requiresReason && !reason.trim();

  // Sales saved on this till while offline belong in this drawer: close
  // only once they've reached the server, or the count won't add up.
  const offline = useOfflineOptional();
  const { items: outboxItems } = useOutbox({
    companyId: offline?.scope.companyId ?? null,
    businessId: offline?.scope.businessId ?? null,
  });
  const unsent = outboxItems.filter(
    (i) =>
      i.type === "order.create" &&
      i.status !== "done" &&
      ((i.payload as { offlineShiftId?: string }).offlineShiftId === shift._id ||
        (!(i.payload as { offlineShiftId?: string }).offlineShiftId && i.userId === offline?.scope.userId)),
  ).length;
  const blockedByUnsent = unsent > 0 && !isAdminClose;

  const canSubmit =
    !blockedByUnsent &&
    cashCount !== "" &&
    Number.isFinite(Number(cashCount)) &&
    Number(cashCount) >= 0 &&
    !reasonMissing &&
    !closeMutation.isPending;

  async function onSubmit() {
    try {
      const result = await closeMutation.mutateAsync({
        id: shift._id,
        input: {
          closingCashCountMinor: Math.round(Number(cashCount) * 100),
          varianceReason: reason.trim() || undefined,
          closingNotes: notes.trim() || undefined,
        },
      });

      const variance = result.varianceMinor || 0;
      const cls = result.varianceClass;
      if ((result as { blind?: boolean }).blind) {
        // Blind close: no result for the cashier — the manager reviews it.
        toast.success(`Shift ${result.shiftNumber} closed — your count is recorded. Your manager will check the drawer.`);
        close(false);
        return;
      }
      if (cls === "balanced") {
        toast.success(`Shift ${result.shiftNumber} closed — balanced ✓`);
      } else if (cls === "minor") {
        toast.warning(
          `Shift ${result.shiftNumber} closed — minor variance ${fmtGHS(variance / 100)}`,
        );
      } else {
        toast.error(
          `Shift ${result.shiftNumber} closed — significant variance ${fmtGHS(variance / 100)}. Logged for review.`,
        );
      }

      close(false);

      // Open Z-report PDF via axios+blob (auth + base URL applied).
      setTimeout(async () => {
        try {
          await shiftsService.openZReportPdf(result._id);
        } catch {
          toast.message("Z-report ready", {
            description: "Open it from your shift history when you're ready.",
          });
        }
      }, 500);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(err?.response?.data?.message || "Failed to close shift");
    }
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <StopCircle className="h-4 w-4 text-[#0A2540]" />
            {isAdminClose ? "Force-close shift" : "Close shift"}
          </DialogTitle>
          <DialogDescription>
            {blind
              ? "Count every note and coin in the drawer. Your manager checks the count against what's expected."
              : "Count the cash physically in the drawer. The system will check it against expected and generate your Z-report."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {unsent > 0 && (
            <div role="alert" className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
              <p className="font-medium">
                {unsent} sale{unsent === 1 ? "" : "s"} made offline {unsent === 1 ? "hasn't" : "haven't"} been sent yet
              </p>
              <p className="text-xs">
                {isAdminClose
                  ? "They're on the cashier's till. Closing now means the drawer count won't include them."
                  : "Connect to the internet and wait for them to send (see the bar at the bottom), then close. Otherwise your drawer won't add up."}
              </p>
            </div>
          )}
          <div className="rounded-md border bg-[#0A2540]/5 border-[#0A2540]/20 p-3">
            <div className="flex items-center justify-between text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  Shift
                </div>
                <div className="font-medium">{shift.shiftNumber}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  Cashier
                </div>
                <div className="font-medium">{shift.cashierName}</div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-md border p-3">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                <Wallet className="h-3 w-3" />
                Opening cash
              </div>
              <div className="mt-1 text-lg font-semibold tabular-nums">
                {fmtGHS(openingCashCedis)}
              </div>
            </div>
            <div className="rounded-md border p-3">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                <TrendingUp className="h-3 w-3" />
                Expected in drawer
              </div>
              <div className="mt-1 text-lg font-semibold tabular-nums">
                {blind
                  ? "Checked by your manager"
                  : typeof expectedFromPreview === "number"
                    ? fmtGHS(expectedFromPreview)
                    : "Calculated at close"}
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor="cash-count">Physical cash count ({curSymbol()})</Label>
              <button
                type="button"
                className="text-xs font-medium text-primary hover:underline"
                onClick={() => setByNotes((v) => !v)}
              >
                {byNotes ? "Type the total instead" : "Count notes and coins"}
              </button>
            </div>
            {byNotes && <DenominationCounter currency={getCurrencyCode()} onTotal={onCounted} />}
            <Input
              id="cash-count"
              type="number"
              min="0"
              step="0.01"
              value={cashCount}
              onChange={(e) => setCashCount(e.target.value)}
              placeholder="0.00"
              readOnly={byNotes}
              autoFocus
              className="text-lg font-medium tabular-nums"
            />
            <p className="text-[11px] text-muted-foreground">
              Count every note and coin in the drawer. Don't include card or
              momo receipts.
            </p>
          </div>

          {liveVariance !== null && cashCount !== "" && (
            <VarianceCard variance={liveVariance} cls={varianceClass} />
          )}

          {requiresReason && (
            <div className="space-y-1.5">
              <Label htmlFor="reason" className="text-rose-600">
                Variance reason <span aria-hidden>*</span>
              </Label>
              <Textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                placeholder="Required — what caused the difference? (logged to audit trail)"
                className="border-rose-200 focus:ring-rose-500"
              />
            </div>
          )}
          {varianceClass === "minor" && (
            <div className="space-y-1.5">
              <Label htmlFor="reason-minor">Variance note (optional)</Label>
              <Input
                id="reason-minor"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Anything we should know?"
              />
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="close-notes">Notes (optional)</Label>
            <Textarea
              id="close-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Anything else to remember about this shift…"
            />
          </div>
        </div>

        <DialogFooter className="flex-col-reverse sm:flex-row">
          <Button variant="ghost" onClick={() => close(false)}>
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            disabled={!canSubmit}
            className="bg-[#0A2540] hover:bg-[#0E2C44] text-white"
          >
            {closeMutation.isPending ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                Closing…
              </>
            ) : (
              <>{blind ? "Close shift" : "Close shift and view Z-report"}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VarianceCard({
  variance,
  cls,
}: {
  variance: number;
  cls: string | null;
}) {
  const isShort = variance < 0;
  const abs = Math.abs(variance);

  if (cls === "balanced") {
    return (
      <div className="rounded-md border border-[#4CAF50]/30 bg-[#4CAF50]/5 p-3">
        <div className="flex items-center gap-2 text-[#4CAF50]">
          <CheckCircle2 className="h-4 w-4" />
          <span className="text-sm font-medium">Balanced</span>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          {fmtGHS(abs)} {isShort ? "short" : "over"} — within the ±{formatMajor(5)} balanced
          range. No action needed.
        </p>
      </div>
    );
  }
  if (cls === "minor") {
    return (
      <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/20 p-3">
        <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
          <AlertTriangle className="h-4 w-4" />
          <span className="text-sm font-medium">Minor variance</span>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          {fmtGHS(abs)} {isShort ? "short" : "over"}. A note is appreciated but
          not required.
        </p>
      </div>
    );
  }
  return (
    <div className="rounded-md border border-rose-300 bg-rose-50 dark:bg-rose-950/20 p-3">
      <div className="flex items-center gap-2 text-rose-700 dark:text-rose-400">
        <AlertOctagon className="h-4 w-4" />
        <span className="text-sm font-medium">Significant variance</span>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        {fmtGHS(abs)} {isShort ? "short" : "over"} — a reason is required and
        this will be flagged to administrators.
      </p>
    </div>
  );
}
